import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { CoreModule } from '../../core/core.module';

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [CommonModule, RouterModule, CoreModule],
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
})
export class BreadcrumbComponent implements OnInit {
  breadcrumbs: Breadcrumb[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.breadcrumbs = this.buildBreadcrumbs(this.route.root);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        this.breadcrumbs = this.buildBreadcrumbs(this.route.root);
      });
  }

  buildBreadcrumbs(
    route: ActivatedRoute,
    url: string = '',
    breadcrumbs: Breadcrumb[] = [],
  ): Breadcrumb[] {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url
        .map((segment) => segment.path)
        .join('/');

      if (routeURL !== '') {
        url += `/${routeURL}`;

        const parent = child.snapshot.data['parent'];
        if (parent) {
          const parentUrl = this.getParentUrl(parent, breadcrumbs);
          const parentBreadcrumb: Breadcrumb = {
            label: this.getParentLabel(parent),
            url: parentUrl,
          };

          if (
            !breadcrumbs.find((crumb) => crumb.label === parentBreadcrumb.label)
          ) {
            breadcrumbs.push(parentBreadcrumb);
          }
        }

        breadcrumbs.push({
          label: child.snapshot.data['breadcrumb'] || routeURL,
          url: routeURL === 'dashboard' ? '/dashboard/home' : url,
        });
      }

      return this.buildBreadcrumbs(child, url, breadcrumbs);
    }

    return breadcrumbs;
  }

  private getParentLabel(parent: string): string {
    const parentRoute = this.router.config.find(
      (route) => route.path === parent || route.data?.['breadcrumb'] === parent,
    );
    return parentRoute && parentRoute.data
      ? parentRoute.data['breadcrumb'] || parent
      : parent;
  }

  private getParentUrl(parent: string, breadcrumbs: Breadcrumb[]): string {
    const parentBreadcrumb = breadcrumbs.find(
      (crumb) => crumb.label === this.getParentLabel(parent),
    );

    if (parentBreadcrumb) {
      return parentBreadcrumb.url;
    }
    return `/dashboard/${parent}`;
  }
}

interface Breadcrumb {
  label: string;
  url: string;
}
